import { useCallback, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import {
  Modal,
  Button,
  useTheme,
  Grid,
  Box,
  Text,
} from '@simplywallst/ui-core';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import { cacheResults } from '@/components/Search/redux/routines';
import BrazeContentCard from '@/components/BrazeContentCard';

import type { Watchlist } from '@/hooks/useWatchlist';
import useWatchlistMutations from '../../hooks/useWatchlistMutations';

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch')
);

const BasicSearch = loadable(
  () => import('@/components/Search/components/BasicSearch')
);
const LimitModal = loadable(() => import('../../components/LimitModal'));

type Props = {
  watchlist: Watchlist;
};

export const Footer = ({ watchlist }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const theme = useTheme();
  const ticker = useRef('');
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const { addCompany, addCompanyStatus } = useWatchlistMutations();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'footer',
  });

  const { id: watchlistId, items, itemsLimit } = watchlist;

  const handleClick = useCallback(() => {
    if (items.length === itemsLimit) {
      setShowLimitModal(true);
      return;
    }
    setShowSearch((prevState) => !prevState);
    trackEvent({
      type: 'track',
      action: 'click',
    });
  }, [items, itemsLimit, trackEvent]);

  const handleClose = useCallback(() => {
    setShowSearch(false);
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      addCompany({ id, includeSnackBar: true, watchlistId });
      setQuery('');
      ticker.current = id.substring(id.indexOf(':') + 1);
      setShowSearch(false);
    },
    [addCompany, watchlistId]
  );

  const getResult = useCallback(
    (
      child: ReactNode,
      result: {
        uniqueSymbol: string;
      }
    ) => (
      <Box
        cursor="pointer"
        role="button"
        onClick={() => {
          handleSelect(result.uniqueSymbol);
        }}
      >
        {child}
      </Box>
    ),
    [handleSelect]
  );

  return (
    <Track>
      <Grid paddingY="x1" gap={`${theme.x2spacePx}px`}>
        <BrazeContentCard feedType="watchlist.bottom" anchorTarget="_blank" />
        <Grid
          templateColumns="1fr"
          templateColumnsLg="max-content max-content"
          justifyContent="space-between"
          gap="8px"
          paddingBottom="x4"
          paddingX="x2"
          paddingXLg="x0"
        >
          {!showSearch ? (
            <Button
              styleType="gentleLarge"
              display="block"
              displayLgUp="inline"
              onClick={handleClick}
              data-cy-id="add-stock"
              disabled={addCompanyStatus === 'pending'}
            >
              <PlusSolid style={{ marginRight: 2 }} /> Add stock
            </Button>
          ) : isMobileScreen ? (
            <Modal isNaked onSecondaryClick={handleClose}>
              <AlgoliaSearch
                onClose={handleClose}
                onSearchResult={(results, query) => {
                  setQuery(query);
                  dispatch(cacheResults.trigger(results));
                }}
                onReset={() => setQuery('')}
                query={query}
                primaryWrapper={getResult}
                secondaryWrapper={getResult}
              />
            </Modal>
          ) : (
            <Box width="260px" style={{ paddingBottom: 350 }}>
              <BasicSearch
                name="search-add-company"
                placeholder="Search e.g. Apple"
                onClick={handleSelect}
              />
            </Box>
          )}
          <Text typography="tiny" color="textSofter">
            {items.length}/{watchlist.itemsLimit} stocks
          </Text>
        </Grid>
      </Grid>
      {showLimitModal && (
        <LimitModal
          watchlist={watchlist}
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </Track>
  );
};
